import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  FiAward,
  FiBriefcase,
  FiCalendar,
  FiCheck,
  FiMapPin,
  FiPlus,
} from "react-icons/fi";
import Header from "../components/header/Header";
import ProgressBar from "../components/progressBar/ProgressBar";
import {
  adminGuide,
  domiciliationGuide,
  juridiqueGuide,
  oapiGuide,
} from "../components/guide/guideContent";
import {
  CREATION_STEPS,
  Details,
  PeriodPicker,
  PriceBlock,
  QuantityStepper,
  SummaryBar,
  billingFor,
  cartItemFor,
  isUnique,
  showError,
} from "../components/options/OptionParts";
import { addToCart, removeFromCart } from "../redux/actions/CartActions";
import {
  addToServiceList,
  removeFromServiceList,
} from "../redux/actions/ServiceListActions";
import { updateBusinessRequest } from "../redux/actions/RequestActions";
import pricing from "../assets/data/pricing.json";
import serviceCode from "../assets/data/serviceCode.json";
import { formatPrice } from "../utils/formatPrice";
import { clampQuantity } from "../utils/creationFlow";
import {
  amountFor,
  identityServices,
  quantityLabel,
  unitHint,
} from "../components/options/identityServices";
import {
  FLOW_REQUESTS,
  OHADA_COUNTRIES,
  adminPrices,
  companyCountry,
  companyTurnover,
} from "../utils/serviceFlows";
import "./styles/options.css";

// Optional services of each flow, the main service of the flow is left out
const buildOptionalServices = ({ flow, country, turnover }) => {
  const isCameroon = country === "CM";
  const mainKey = FLOW_REQUESTS[flow]?.mainKey;

  return [
    flow === "creation" && {
      key: "oapi",
      icon: FiAward,
      title: "Protection du nom à l'OAPI",
      summary:
        "Protégez le nom de votre entreprise dans les 17 États membres de l'OAPI. Votre nom est protégé dès le dépôt.",
      prices: { unique: pricing.oapi },
      guide: oapiGuide,
    },
    flow !== "creation" &&
      OHADA_COUNTRIES.includes(country) && {
        key: "domiciliation",
        icon: FiMapPin,
        title: "Domiciliation",
        summary:
          "Une adresse professionnelle pour votre siège social, avec la gestion de votre courrier.",
        prices: pricing.domiciliation,
        guide: domiciliationGuide,
      },
    isCameroon && {
      key: "platformAdmin",
      icon: FiCalendar,
      title: "Administration",
      summary:
        "Vos télédéclarations mensuelles, votre DSF et vos cotisations sociales prises en charge par nos équipes.",
      prices: adminPrices(pricing.platformAdmin, turnover),
      guide: adminGuide,
    },
    {
      key: "platformJuridique",
      icon: FiBriefcase,
      title: "Suivi juridique",
      summary:
        "Contentieux, contrats d'affaires et secrétariat juridique pour sécuriser votre activité.",
      prices: pricing.platformJuridique,
      guide: juridiqueGuide,
    },
  ].filter((option) => option && option.key !== mainKey);
};

function Options() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const cart = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.authUser);
  const { services } = useSelector((state) => state.serviceList);
  const requests = useSelector((state) => state.request);
  const { company } = useSelector((state) => state.companies);

  const fromCart = new URLSearchParams(location.search).get("from") === "cart";
  const flow = cart?.from;
  const isCreation = flow === "creation";
  const flowRequest = FLOW_REQUESTS[flow];
  const country = companyCountry(cart);
  const turnover = companyTurnover(cart);

  const optionalServices = useMemo(
    () => buildOptionalServices({ flow, country, turnover }),
    [flow, country, turnover],
  );
  const allOptions = useMemo(
    () => [...optionalServices, ...identityServices],
    [optionalServices],
  );

  // Selection is restored from the cart so the page can also be used to edit options
  const [selection, setSelection] = useState(() =>
    Object.fromEntries(
      [...optionalServices, ...identityServices].map((option) => [
        option.key,
        {
          selected: Boolean(cart?.[option.key]),
          periodicity: cart?.[option.key]?.periodicity || "mensuel",
          quantity: option.rule
            ? clampQuantity(option.key, cart?.[option.key]?.quantity)
            : undefined,
        },
      ]),
    ),
  );
  const [openDetails, setOpenDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const toggleOption = (key) =>
    setSelection((prev) => ({
      ...prev,
      [key]: { ...prev[key], selected: !prev[key].selected },
    }));

  const setOptionPeriod = (key, periodicity) =>
    setSelection((prev) => ({
      ...prev,
      [key]: { ...prev[key], periodicity },
    }));

  // Changing the quantity also adds the service
  const setOptionQuantity = (key, quantity) =>
    setSelection((prev) => ({
      ...prev,
      [key]: { ...prev[key], quantity, selected: true },
    }));

  const selectedOptions = allOptions.filter(
    (option) => selection[option.key]?.selected,
  );
  const total = selectedOptions.reduce(
    (sum, option) => sum + amountFor(option, selection[option.key]),
    0,
  );

  const requestServiceFor = (option) => {
    const { periodicity, quantity } = selection[option.key];

    return option.rule
      ? { code: serviceCode[option.key], billing: "lifetime", quantity }
      : {
          code: serviceCode[option.key],
          billing: billingFor(option.prices, periodicity),
        };
  };

  const handleContinue = async () => {
    setSubmitting(true);

    const optionCodes = allOptions.map((option) => serviceCode[option.key]);
    const updatedServices = [
      ...services.filter((service) => !optionCodes.includes(service.code)),
      ...selectedOptions.map(requestServiceFor),
    ];

    if (isCreation) {
      await dispatch(
        updateBusinessRequest(
          user.token,
          requests.businessCreationRequest?.reference,
          { services: updatedServices },
          handleSaved,
        ),
      );
      return;
    }

    if (!flowRequest) {
      setSubmitting(false);
      showError(Swal, "Ce service ne propose pas d'options.");
      return;
    }

    // Other flows create their request here, or update it when coming back
    const existingRequest = requests[flowRequest.storeKey];

    await dispatch(
      existingRequest?.reference
        ? flowRequest.update(
            user.token,
            existingRequest.reference,
            { services: updatedServices },
            handleSaved,
          )
        : flowRequest.create(
            user.token,
            {
              company: company?._id || cart?.company?._id,
              services: updatedServices,
            },
            handleSaved,
          ),
    );
  };

  const handleSaved = async ({ success, message }) => {
    if (!success) {
      setSubmitting(false);
      showError(Swal, message);
      return;
    }

    for (const option of allOptions) {
      const state = selection[option.key];
      const code = serviceCode[option.key];

      if (state.selected) {
        await dispatch(
          addToCart({
            [option.key]: option.rule
              ? { amount: amountFor(option, state), quantity: state.quantity }
              : cartItemFor(option.prices, state.periodicity),
          }),
        );
        await dispatch(addToServiceList(requestServiceFor(option)));
      } else {
        await dispatch(removeFromCart(option.key));
        await dispatch(removeFromServiceList(code));
      }
    }

    setSubmitting(false);
    navigate("/cart");
  };

  const renderCard = (option) => {
    const state = selection[option.key];
    const Icon = option.icon;

    return (
      <article
        key={option.key}
        className={`option-card ${state.selected ? "is-selected" : ""}`}
      >
        <div className="option-card-top">
          <span className="option-icon" aria-hidden="true">
            <Icon />
          </span>
          <button
            type="button"
            className="option-toggle"
            aria-pressed={state.selected}
            onClick={() => toggleOption(option.key)}
          >
            {state.selected ? (
              <>
                <FiCheck aria-hidden="true" /> Ajouté
              </>
            ) : (
              <>
                <FiPlus aria-hidden="true" /> Ajouter
              </>
            )}
          </button>
        </div>

        <h2 className="option-title">{option.title}</h2>
        <p className="option-summary">{option.summary}</p>

        <div className="option-card-pricing">
          {option.rule ? (
            <>
              <QuantityStepper
                value={state.quantity}
                min={option.rule.min}
                step={option.rule.step}
                unitLabel={quantityLabel(option, state.quantity)}
                onChange={(quantity) => setOptionQuantity(option.key, quantity)}
              />
              <p className="option-price">
                <strong>{formatPrice(amountFor(option, state))}</strong>
              </p>
              <p className="option-unit">{unitHint(option)}</p>
            </>
          ) : (
            <>
              {!isUnique(option.prices) && (
                <PeriodPicker
                  value={state.periodicity}
                  onChange={(periodicity) =>
                    setOptionPeriod(option.key, periodicity)
                  }
                />
              )}
              <PriceBlock
                prices={option.prices}
                periodicity={state.periodicity}
              />
            </>
          )}
        </div>

        {option.guide && (
          <div className="option-card-footer">
            <Details
              guide={option.guide}
              open={openDetails === option.key}
              onToggle={() =>
                setOpenDetails((current) =>
                  current === option.key ? null : option.key,
                )
              }
            />
          </div>
        )}
      </article>
    );
  };

  const count = selectedOptions.length;
  const label =
    count === 0
      ? "Aucun service ajouté"
      : `${count} service${count > 1 ? "s" : ""} ajouté${count > 1 ? "s" : ""}`;

  return (
    <div className="options-page">
      <Header />
      <ProgressBar steps={CREATION_STEPS} currentStep="Services" />

      <div className="container options-container">
        <header className="options-header">
          <h1 className="options-title">
            Complétez votre {isCreation ? "création" : "commande"} avec nos{" "}
            <span>services</span>
          </h1>
          <p className="options-lead">
            Ajoutez les services utiles à votre entreprise. Ils sont facultatifs
            et modifiables depuis votre panier.
          </p>
        </header>

        <div className="options-grid">{optionalServices.map(renderCard)}</div>

        <h2 className="options-section-title">Identité visuelle</h2>
        <p className="options-section-lead">
          Donnez une image professionnelle à votre entreprise dès son lancement.
        </p>
        <div className="options-grid">{identityServices.map(renderCard)}</div>
      </div>

      <SummaryBar
        label={label}
        total={count === 0 ? "—" : `+ ${formatPrice(total)}`}
        buttonLabel={
          submitting
            ? "Enregistrement…"
            : fromCart
              ? "Enregistrer et revenir au panier"
              : count === 0
                ? "Continuer sans service"
                : "Continuer"
        }
        disabled={submitting}
        onContinue={handleContinue}
      />
    </div>
  );
}

export default Options;
